/* HelpPage — Mode d'emploi articles
 *
 * Used by 3 routes:
 *   /portal/help (membre)
 *   /press/help  (presse)
 *   /admin/help  (admin — édition possible)
 *
 * Layout: left sidebar = categories | center = article body | top = search bar.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, BookOpen, Edit3, Trash2, Plus, X, Save, ChevronRight, Settings } from "lucide-react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function HelpPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeSlug, setActiveSlug] = useState(searchParams.get("slug") || "");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const sidebar = location.pathname.startsWith("/admin") ? "admin"
    : location.pathname.startsWith("/press") ? "press" : "portal";

  const selectSlug = (slug) => {
    setActiveSlug(slug);
    if (slug) setSearchParams({ slug }, { replace: true });
    else setSearchParams({}, { replace: true });
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/help-articles");
      setArticles(r.data.items || []);
      setCategories(r.data.categories || []);
      if (!activeSlug && r.data.items?.length) {
        selectSlug(r.data.items[0].slug);
      }
    } catch (e) {
      toast.error("Erreur de chargement de la documentation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter((a) =>
      (a.title || "").toLowerCase().includes(q) ||
      (a.excerpt || "").toLowerCase().includes(q) ||
      (a.category || "").toLowerCase().includes(q) ||
      (a.body_html || "").toLowerCase().includes(q)
    );
  }, [articles, query]);

  const grouped = useMemo(() => {
    const map = {};
    for (const a of filtered) {
      const cat = a.category || "Général";
      (map[cat] = map[cat] || []).push(a);
    }
    return map;
  }, [filtered]);

  // "🆕 Nouveautés" — top 5 articles sorted by updated_at DESC.
  // `react-hooks/purity` requires us to avoid Date.now() inside useMemo, so we sort
  // unconditionally and let the rendered timestamp string be computed at JSX render time.
  const recentArticles = useMemo(() => {
    return [...filtered]
      .filter((a) => !!a.updated_at)
      .sort((x, y) => (y.updated_at || "").localeCompare(x.updated_at || ""))
      .slice(0, 5);
  }, [filtered]);

  const active = articles.find((a) => a.slug === activeSlug) || filtered[0] || null;

  const seedDefaults = async () => {
    if (!window.confirm("Initialiser les articles par défaut ? Les articles existants ne seront pas écrasés.")) return;
    try {
      const r = await api.post("/admin/help-articles/seed", {});
      toast.success(`${r.data.created} créés, ${r.data.updated} complétés`);
      load();
    } catch (e) { toast.error("Erreur seed"); }
  };

  const saveArticle = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await api.patch(`/admin/help-articles/${editing.id}`, editing);
      } else {
        const r = await api.post("/admin/help-articles", editing);
        setActiveSlug(r.data.slug);
        setSearchParams({ slug: r.data.slug }, { replace: true });
      }
      toast.success("Article enregistré");
      setEditing(null);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const deleteArticle = async (a) => {
    if (!window.confirm(`Supprimer "${a.title}" ?`)) return;
    try {
      await api.delete(`/admin/help-articles/${a.id}`);
      toast.success("Article supprimé");
      if (activeSlug === a.slug) selectSlug("");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const startCreate = () => {
    setCreating(true);
    setEditing({
      slug: "", title: "", body_html: "<p>Décrivez ici…</p>",
      category: categories[0] || "Général",
      roles: ["member"], order: 100, icon: "💡", excerpt: "",
    });
  };

  return (
    <PortalLayout sidebar={sidebar}>
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="help-page">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl"><BookOpen className="mr-2 inline h-7 w-7 text-wine-600" /> Mode d&apos;emploi</h1>
          <p className="mt-1 text-neutral-600">Documentation des fonctionnalités accessibles pour votre rôle.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button onClick={seedDefaults} className="inline-flex items-center gap-1.5 rounded-full border border-wine-200 bg-white px-4 py-2 text-xs font-bold text-wine-700 hover:bg-wine-50" data-testid="help-seed-btn">
              <Settings className="h-3.5 w-3.5" /> Initialiser par défaut
            </button>
            <button onClick={startCreate} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="help-create-btn">
              <Plus className="h-3.5 w-3.5" /> Nouvel article
            </button>
          </div>
        )}
      </div>

      {/* Search bar */}
      <div className="mt-6 flex items-center gap-3 rounded-2xl border border-wine-100 bg-white px-4 py-2.5 shadow-sm">
        <Search className="h-4 w-4 text-neutral-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher dans la doc (titre, contenu, catégorie)…"
          className="w-full bg-transparent text-sm outline-none placeholder:text-neutral-400"
          data-testid="help-search-input"
        />
        {query && <button onClick={() => setQuery("")} className="text-xs text-neutral-400 hover:text-neutral-600">Effacer</button>}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[260px_1fr]">
        {/* Categories sidebar */}
        <aside className="rounded-2xl border border-neutral-100 bg-white p-3" data-testid="help-sidebar">
          {loading && <p className="px-3 py-2 text-sm text-neutral-500">Chargement…</p>}
          {!loading && Object.keys(grouped).length === 0 && (
            <p className="px-3 py-4 text-sm text-neutral-500">
              Aucun article {isAdmin ? "— cliquez \"Initialiser par défaut\" pour les générer." : "pour le moment."}
            </p>
          )}

          {/* "🆕 Nouveautés" pinned section — last 30 days, top 5 */}
          {recentArticles.length > 0 && (
            <div className="mb-4 rounded-xl border border-wine-200 bg-gradient-to-br from-wine-50/60 to-amber-50/30 p-2" data-testid="help-recent-section">
              <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-wine-700 flex items-center gap-1">
                <span>🆕</span> Nouveautés
                <span className="ml-auto rounded-full bg-wine-500 px-1.5 text-[9px] font-bold text-white">{recentArticles.length}</span>
              </div>
              <ul>
                {recentArticles.map((a) => {
                  const isActive = a.slug === activeSlug;
                  const d = a.updated_at ? new Date(a.updated_at) : null;
                  const ago = d ? d.toLocaleDateString("fr", { day: "numeric", month: "short" }) : "";
                  // "Nouveau" badge = created in the last 14 days AND created_at ≈ updated_at
                  // (i.e., not just re-edited). Otherwise → "Mis à jour".
                  const createdAt = a.created_at ? new Date(a.created_at) : null;
                  const isFresh = createdAt && d && Math.abs(d.getTime() - createdAt.getTime()) < 24 * 60 * 60 * 1000;
                  const ageDays = createdAt ? Math.floor((new Date().getTime() - createdAt.getTime()) / (24 * 60 * 60 * 1000)) : 999;
                  const showNew = isFresh && ageDays <= 14;
                  return (
                    <li key={`recent-${a.id}`}>
                      <button
                        onClick={() => selectSlug(a.slug)}
                        className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${
                          isActive ? "bg-wine-100 font-semibold text-wine-800" : "text-neutral-700 hover:bg-wine-50/60"
                        }`}
                        data-testid={`help-recent-link-${a.slug}`}
                      >
                        <span className="flex items-center gap-1.5 truncate min-w-0">
                          {a.icon && <span className="shrink-0">{a.icon}</span>}
                          <span className="truncate">{a.title}</span>
                          {showNew && (
                            <span className="ml-0.5 shrink-0 rounded-full bg-rose-500 px-1.5 py-0 text-[9px] font-bold text-white" data-testid={`help-new-badge-${a.slug}`}>
                              NEW
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-[10px] font-normal text-neutral-500">{ago}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="mb-3">
              <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-wine-700">{cat}</div>
              <ul>
                {items.map((a) => {
                  const isActive = a.slug === activeSlug;
                  return (
                    <li key={a.id}>
                      <button
                        onClick={() => selectSlug(a.slug)}
                        className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm transition-colors ${isActive ? "bg-wine-50 font-semibold text-wine-800" : "text-neutral-700 hover:bg-neutral-50"}`}
                        data-testid={`help-link-${a.slug}`}
                      >
                        <span className="flex items-center gap-2 truncate">
                          {a.icon && <span className="shrink-0">{a.icon}</span>}
                          <span className="truncate">{a.title}</span>
                        </span>
                        {isActive && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-wine-600" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </aside>

        {/* Article body */}
        <div className="rounded-2xl border border-neutral-100 bg-white p-6 sm:p-8" data-testid="help-article">
          {active ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-wine-600">{active.category}</div>
                  <h2 className="mt-1 font-serif text-3xl text-navy">{active.icon} {active.title}</h2>
                  {active.excerpt && <p className="mt-2 text-sm text-neutral-600">{active.excerpt}</p>}
                </div>
                {isAdmin && (
                  <div className="flex shrink-0 gap-1">
                    <button onClick={() => setEditing({ ...active })} className="grid h-9 w-9 place-items-center rounded-full text-navy hover:bg-navy/10" title="Modifier" data-testid={`help-edit-${active.slug}`}><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => deleteArticle(active)} className="grid h-9 w-9 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`help-delete-${active.slug}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
              <div className="prose prose-neutral mt-6 max-w-none text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: active.body_html }} />
              {active.updated_at && (
                <p className="mt-6 border-t border-neutral-100 pt-3 text-[11px] text-neutral-400">
                  Dernière mise à jour : {new Date(active.updated_at).toLocaleString("fr")} {active.updated_by && `· par ${active.updated_by}`}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-neutral-500">Sélectionnez un article dans la liste de gauche.</p>
          )}
        </div>
      </div>

      {/* Edit / Create modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="help-edit-modal">
          <button className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => { setEditing(null); setCreating(false); }} aria-label="Fermer" />
          <div className="relative w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-navy">{creating ? "✨ Nouvel article" : "✏️ Modifier l'article"}</h2>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="grid h-8 w-8 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Titre *" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="help-edit-title" />
              <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="slug-url (optionnel, auto)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" data-testid="help-edit-slug" />
              <input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="Catégorie *" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="help-edit-category" />
              <input value={editing.icon || ""} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} placeholder="Icône emoji (ex: 📅)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="help-edit-icon" />
              <input value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} placeholder="Excerpt (résumé court)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" data-testid="help-edit-excerpt" />
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Rôles autorisés</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {["public", "member", "press", "admin"].map((r) => (
                    <label key={r} className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold ${editing.roles?.includes(r) ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white text-neutral-700"}`}>
                      <input type="checkbox" checked={editing.roles?.includes(r) || false} onChange={(e) => {
                        const set = new Set(editing.roles || []);
                        if (e.target.checked) set.add(r); else set.delete(r);
                        setEditing({ ...editing, roles: Array.from(set) });
                      }} className="hidden" />
                      {r}
                    </label>
                  ))}
                </div>
              </div>
              <textarea value={editing.body_html || ""} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} placeholder="Contenu HTML (titres <h3>, listes <ul>, liens <a>, gras <strong>…)" className="h-56 resize-y rounded-xl border border-neutral-200 px-3 py-2 font-mono text-xs sm:col-span-2" data-testid="help-edit-body" />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setEditing(null); setCreating(false); }} className="rounded-full border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-50">Annuler</button>
              <button onClick={saveArticle} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="help-edit-save">
                <Save className="h-4 w-4" /> Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
